import { switchMap } from "rxjs/operators";
import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { IcsApprover as ICsApprover } from "../interfaces/csApprover";
import { IRatePlanOptionDTO } from "../interfaces/ratePlanOption";
import { ICsApprovedRequest, IThemeDetails } from "../interfaces/themeDetail";
import { DSRESTClient } from "../shared/communication-objects/restclient.service";
import { api, part } from "../shared/communication-objects/api-path";
import { ApiSuccessfulResponse } from "../shared/models/apiSuccessfulResponse";
import { AuthService } from "./auth.service";
import { LogService } from "./log.service";
import { ThemeStatusEnum, ThemeStatusDto } from "../modules/theming/theme-details/theme-approval/theme-approval.component";
import { HttpParams } from "@angular/common/http";

@Injectable()
export class ThemingService {
  private readonly apiUrl = api`theming`;

  constructor(
    private restClient: DSRESTClient,
    private logger: LogService,
    private authService: AuthService
  ) {}


  activateTheme(themeId: number) {
    return this.restClient.get(api`${this.apiUrl}/ActivateTheme/${themeId}`);
  }

  canCreateTheme(): Observable<boolean> {
    //Authorize Link to Edit Theme
    return this.authService.passesPolicyOf(
      this.authService.securityPolicies.RequiresManageThemes
    );
  }

  canEditTheme(): Observable<boolean> {
    return this.authService.passesPolicyOf(
      this.authService.securityPolicies.RequiresManageThemes
    );
  }

  createTheme(
    themeDetails: IThemeDetails,
    imageFile: any = null
  ): Observable<any> {
    const formData = new FormData();
    formData.append("themeDetails", JSON.stringify(themeDetails));
    if (imageFile) {
      this.logger.debug("createTheme", themeDetails, imageFile);
      formData.append("imageFile", imageFile);
    }
    return this.restClient.post(api`${this.apiUrl}/CreateTheme`, formData);
  }

  getTheme(themeId: number): Observable<ApiSuccessfulResponse<IThemeDetails>> {
    return this.restClient.get<IThemeDetails>(api`${this.apiUrl}/${themeId}`);
  }

  getThemes(orgId: number): Observable<any> {
    return this.restClient.get(api`${this.apiUrl}/GetOrganizationThemes/${orgId}`);
  }

  getRatePlans(orgId: number): Observable<any> {
    return this.restClient.get<IRatePlanOptionDTO[]>(
      api`${this.apiUrl}/GetRatePlanOptions/${orgId}`
    );
  }

  getApprovalStatus(themeId: number): Observable<ThemeStatusDto> {
    return this.restClient.getDirect<ThemeStatusDto>(
      api`${this.apiUrl}/get-approval-status/${themeId}`
    );
  }


  getApprovalPermissions(themeId: number): Observable<Record<ThemeStatusEnum, boolean>> {
    return this.restClient.getDirect<Record<ThemeStatusEnum, boolean>>(
      api`${this.apiUrl}/get-approval-permissions/${themeId}`
    );
  }

  saveApproval(status: ThemeStatusEnum, themeId: number, input: string = null): Observable<any> {
    let params = new HttpParams();
    if (input) {
      params = new HttpParams().set('input', input);
    }
    return this.restClient.post(api`${this.apiUrl}/save-approval/${themeId}/${status}?${params}`, null);
  }

  getCsApprover(
    themeId: number
  ): Observable<ApiSuccessfulResponse<ICsApprover>> {
    return this.restClient.get<ICsApprover>(
      api`${this.apiUrl}/GetCsApprover/${themeId}`
    );
  }

  saveCSApproval(formRequestModel: ICsApprovedRequest): Observable<any> {
    return this.restClient.post(api`${this.apiUrl}/SaveCSApproval`, formRequestModel);
  }

  saveThemeDetails(
    themeDetails: IThemeDetails,
    imageFile: any = null
  ): Observable<any> {
    const formData = new FormData();
    formData.append("themeDetails", JSON.stringify(themeDetails));
    if (imageFile) {
      this.logger.debug("saveTheme", themeDetails, imageFile);
      formData.append("imageFile", imageFile);
    }
    return this.restClient.post(api`${this.apiUrl}/SaveTheme`, formData);
  }

  public initializeThemeModel(
    billableOrgId: number
  ): Observable<IThemeDetails> {
    //SBTMTAPWP-1144: Sub-Billable orgs should derive their themes from the parent billable org
    return this.restClient.getDirect<IThemeDetails>(
      api`${this.apiUrl}/GetInitialCreateThemeModel/${billableOrgId}`
    );
  }

  getAvailableCustomerApprovers(
    billableOrgId: number
  ): Observable<ICsApprover[]> {
    return this.restClient.getDirect<ICsApprover[]>(
      api`${this.apiUrl}/GetAvailableCustomerApprovers/${billableOrgId}`
    );
  }

  customerUrlExists(billableOrgId: number, value: string): Observable<boolean> {
    return this.restClient
      .getDirect<{ exists: boolean }>(
        api`${this.apiUrl}/companyurl-already-exists/${billableOrgId}/${part(value)}`
      )
      .pipe(
        switchMap((response) => {
          this.logger.debug(
            `companyurl-already-exists ${billableOrgId}/${value} ${response.exists}`,
            value,
            response
          );
          return of(response.exists);
        })
      );
  }
}
